import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { ItemGroupPreview } from "@dto/architecture.dto";
import { ValidatorRule } from '@rc-component/form/es/interface';
import { Button, Form, Input } from "antd";
import { useContext, useEffect, useRef } from "react";
import { XmInput } from "src/component/input/XmInput";
import { GLOB } from "src/util/Glob";
import { AllItemsContext } from '../comp/AllItems';
import { makePreviewApiData } from "../comp/preview/CgPreviewHandler";
import { usePreviewModal } from "../comp/preview/Preview";
import { ArrayElement } from "../parent/TagList";
import { AdditionalApiPropsContext, RuleContext } from "../XmRule";
import { RuleGroup } from "../XmRuleList";
import AddButton from "./AddButton";

interface Props {
  formItemName?: (string | number)[];
}

function RegexList({ formItemName = ['regex'] }: Props) {
  const { cls, subsystem, hw_type } = useContext(AllItemsContext);
  const { linuxExclude, getPath, getValue } = useContext(RuleContext);
  const { preview, additionalPayload } = useContext(AdditionalApiPropsContext);
  const [showModal, contextHolder] = usePreviewModal();

  const items = formItemName.includes('exclude') ? getValue<RuleGroup['exclude']['regex']>(['exclude', 'regex']) : getValue<RuleGroup['regex']>('regex');
  const init = useRef<() => void>();

  useEffect(() => {
    init.current?.();
  }, []);

  const handlePreview = (regex: string) => () => {
    const parents = getValue<RuleGroup['parents']>('parents');

    if (!formItemName.includes('exclude')) {
      const data = {
        ...makePreviewApiData({
          cls,
          hwType: hw_type,
          subsystem,
          parents,
          regex: [regex],
          ...additionalPayload
        }, linuxExclude),
        ...additionalPayload
      };

      showModal({
        subsystem,
        hw_type,
        data,
        additionalApiParams: preview?.items?.item,
      });

      return;
    }

    const regexList = getValue<RuleGroup['regex']>('regex');
    const tags = getValue<RuleGroup['tags']>('tags');
    const data: ItemGroupPreview[] = [
      { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents, regex: [regex] }, linuxExclude,), ...additionalPayload },
      { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents, regex: regexList, tags }, linuxExclude), ...additionalPayload },
    ];

    showModal({
      subsystem,
      hw_type,
      data,
      additionalApiParams: preview?.exclude?.item ? { ...preview.exclude.item, intersection: true } : { intersection: true },
    });
  };

  const { readonly } = GLOB.userInfo;

  return <>
    <Form.List name={getPath(formItemName)}>
      {(regexItems, { remove, add }) => {

        if (!init.current && !formItemName.includes('exclude') && !items) {
          init.current = () =>
            add('.*');
        }

        return <>
          <div className="xm-rule-filter-regex">
            {regexItems?.map(({ key, name }) => {
              const regex = formItemName.includes('exclude') ?
                getValue<ArrayElement<RuleGroup['exclude']['regex']>>(['exclude', 'regex', name]) :
                getValue<ArrayElement<RuleGroup['regex']>>(['regex', name]);

              return <span className="xm-regex-item" key={`regex-list-${key}`}>
                <Input.Group compact className={!regex.length || !subsystem ? 'xm-disabled-input' : 'xm-enable-input'}>
                  <Form.Item name={name} rules={[{ required: true, message: 'Please enter Regex!' }, validatePattern]}>
                    <XmInput size="small" data-testid="regex-input" />
                  </Form.Item>
                  <Button
                    disabled={!regex.length || !subsystem || !isPatternValid(regex)}
                    size="small"
                    onClick={!(!regex.length || !subsystem) ? handlePreview(regex) : undefined}
                    icon={<EyeOutlined />}
                  />
                </Input.Group>
                <Button
                  size="small"
                  className={(readonly || (items.length < 2)) ? 'xm-disabled-delete' : 'hover-danger'}
                  disabled={readonly || (items.length < 2)}
                  type='link'
                  title='Remove rule'
                  icon={<DeleteOutlined onClick={() => remove(name)} />}
                />
              </span>;
            })}
          </div>
          <AddButton onClick={() => add('.*')} />
        </>;
      }}
    </Form.List>
    {contextHolder}
  </>;
}

export const validatePattern: ValidatorRule = {
  message: "Invalid pattern!",
  validator(_, value: string) {
    try {
      new RegExp(value);
    } catch {
      return Promise.reject(new Error());
    }

    return Promise.resolve();
  }
};

export function isPatternValid(patter: string) {
  try {
    new RegExp(patter);
  } catch {
    return 0;
  }

  return 1;
}

export default RegexList;